import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  CreditCard,
  History,
  Settings,
  ChevronRight,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import subscriptionService from "../../../services/subscriptionService";

// Composants de chargement et d'erreur
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

const ErrorAlert = ({ message, onRetry }) => (
  <div
    className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
    role="alert"
  >
    <strong className="font-bold">Erreur ! </strong>
    <span className="block sm:inline">{message}</span>
    {onRetry && (
      <button
        onClick={onRetry}
        className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
      >
        Réessayer
      </button>
    )}
  </div>
);

const SubscriptionProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [lifetimeProduct, setLifetimeProduct] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [planContents, setPlanContents] = useState([]);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [activeSubscriptionDetails, setActiveSubscriptionDetails] =
    useState(null);
  const [swapModal, setSwapModal] = useState({ isOpen: false, plan: null });
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapError, setSwapError] = useState(null);
  const [swapSuccess, setSwapSuccess] = useState(false);

  // Regrouper les plans par nom de manière dynamique
  const [groupedPlans, setGroupedPlans] = useState({});
  const [isLifeTimeProductActive, setIsLifeTimeProductActive] = useState(false);

  // Structure pour organiser les features par plan
  const [featuresByPlan, setFeaturesByPlan] = useState({});

  // Features communes à tous les plans
  const [commonFeatures, setCommonFeatures] = useState([]);

  // Mot-clé pour "Illimité"
  const UNLIMITED_KEYWORD = "Illimité";

  // Couleurs générées dynamiquement pour les plans
  const generatePlanColor = (index) => {
    const colors = ["blue", "indigo", "teal", "green", "amber", "red", "gray"];
    return colors[index % colors.length];
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Fonction pour organiser les features par plan
  const organizeFeaturesByPlan = (contents) => {
    const featuresMap = {};

    contents.forEach((content) => {
      const planId = content.plan_id;
      if (!featuresMap[planId]) {
        featuresMap[planId] = {
          included: [],
          limits: [],
          params: [],
        };
      }

      if (content.quantity === UNLIMITED_KEYWORD) {
        featuresMap[planId].limits.push({
          ...content,
          isUnlimited: true,
        });
      } else if (
        !isNaN(parseFloat(content.quantity)) &&
        isFinite(content.quantity)
      ) {
        featuresMap[planId].limits.push({
          ...content,
          quantity: parseFloat(content.quantity),
          isNumeric: true,
        });
      } else if (content.subscription_param_type === "boolean") {
        featuresMap[planId].included.push(content);
      } else {
        featuresMap[planId].params.push(content);
      }
    });

    return featuresMap;
  };

  // Fonction pour extraire les features communes
  const extractCommonFeatures = (featuresMap, plansList) => {
    if (Object.keys(featuresMap).length === 0 || plansList.length === 0)
      return [];

    const firstPlanId = plansList[0].id;
    const firstPlanFeatures = featuresMap[firstPlanId]?.limits || [];

    return firstPlanFeatures.filter((feature) => {
      return plansList.every((plan) => {
        const planFeatures = featuresMap[plan.id]?.limits || [];
        return planFeatures.some(
          (f) => f.subscription_param_name === feature.subscription_param_name,
        );
      });
    });
  };

  // Fonction pour regrouper les plans par nom
  const groupPlansByName = (plansList) => {
    return plansList.reduce((groups, plan) => {
      const planName = plan.name;
      if (!groups[planName]) {
        groups[planName] = [];
      }
      groups[planName].push(plan);
      return groups;
    }, {});
  };

  // Fonction pour déterminer le plan recommandé
  const getRecommendedPlanName = () => {
    const planNames = Object.keys(groupedPlans);
    if (planNames.length === 0) return null;

    const nonLifetimePlans = planNames.filter(
      (name) => !groupedPlans[name].some((p) => p.period_name === "lifetime"),
    );

    if (nonLifetimePlans.length === 0) return planNames[0];

    return nonLifetimePlans[Math.floor(nonLifetimePlans.length / 2)];
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await subscriptionService.getPlans();

      if (response.status === 200) {
        const plansData = response.plans || [];
        const contentsData = response.plan_contents?.plan_content_items || [];
        const LifeTimeProductActive = response?.is_life_time_product_active;
        setPlans(plansData);
        setCurrentSubscription(response.subscription || null);
        setPlanContents(contentsData);
        setLifetimeProduct(response?.lifeTime_product);
        const featuresMap = organizeFeaturesByPlan(contentsData);
        setFeaturesByPlan(featuresMap);

        const common = extractCommonFeatures(featuresMap, plansData);
        setCommonFeatures(common);

        const grouped = groupPlansByName(plansData);

        setGroupedPlans(grouped);
        setIsLifeTimeProductActive(LifeTimeProductActive);

        if (response.subscription) {
          const activePlan = plansData.find(
            (plan) =>
              plan.stripe_price_id === response.subscription.stripe_price,
          );
          setActiveSubscriptionDetails(activePlan || null);
        }
      } else if (response.status === 204) {
        setPlans([]);
        setCurrentSubscription(null);
        setActiveSubscriptionDetails(null);
        setGroupedPlans({});
        setFeaturesByPlan({});
        setCommonFeatures([]);
        setIsLifeTimeProductActive(false);
      }
    } catch (err) {
      setError("Impossible de charger les plans d'abonnement");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = (plan) => {
    if (
      currentSubscription &&
      currentSubscription.stripe_price === plan.stripe_price_id
    ) {
      return;
    }
    navigate("/cfp/subscription/checkout", {
      state: {
        plan: plan,
        billingCycle: billingCycle,
        planFeatures: featuresByPlan[plan.id] || {
          included: [],
          limits: [],
          params: [],
        },
      },
    });
  };

  // Fonction pour gérer le changement de plan (swap)
  const handleSwapPlan = async (plan) => {
    if (
      currentSubscription &&
      currentSubscription.stripe_price === plan.stripe_price_id
    ) {
      return;
    }

    setSwapModal({ isOpen: true, plan });
  };

  // Fonction pour confirmer le swap
  // const confirmSwap = async () => {
  //   if (!swapModal.plan) return;

  //   setSwapLoading(true);
  //   setSwapError(null);
  //   setSwapSuccess(false);
  //   console.log(swapModal.plan.stripe_plan_id);

  //   try {
  //     const response = await subscriptionService.swapPlan(
  //       swapModal.plan.stripe_plan_id,
  //     );

  //     if (response.status === 200 && response?.planId) {
  //       const res = await subscriptionService.subscribe(response.planId);

  //       if (res && res.url) {
  //         window.location.href = res.url;
  //       }
  //       // window.open(res.redirect_url, "_blank");
  //       setSwapSuccess(true);
  //       setTimeout(() => {
  //         fetchPlans();
  //         closeSwapModal();
  //       }, 2000);
  //     } else if (response.status === 200) {
  //       setSwapSuccess(true);
  //       setTimeout(() => {
  //         fetchPlans();
  //         closeSwapModal();
  //       }, 2000);
  //     } else {
  //       setSwapError(
  //         response.message ||
  //           "Une erreur est survenue lors du changement de plan",
  //       );
  //     }
  //   } catch (err) {
  //     setSwapError(
  //       err.response?.data?.message || "Impossible de changer de plan",
  //     );
  //     console.error(err);
  //   } finally {
  //     setSwapLoading(false);
  //   }
  // };
  const confirmSwap = async () => {
    if (!swapModal.plan) return;

    setSwapLoading(true);
    setSwapError(null);
    setSwapSuccess(false);

    try {
      const response = await subscriptionService.swapPlan(
        swapModal.plan.stripe_plan_id,
      );

      if (response.status === 200) {
        // Cas avec planId (besoin de souscrire)
        console.log(response);

        if (response.planId) {
          const res = await subscriptionService.subscribe(response.planId);

          if (res?.url) {
            // Redirection unique vers Stripe
            window.location.href = res.url;
            return; // Important : arrêter l'exécution
          }
        }

        // Cas sans planId (déjà traité) ou sans URL de redirection
        setSwapSuccess(true);
        await fetchPlans(); // Pas besoin de setTimeout
        closeSwapModal();
      } else {
        setSwapError(
          response.message ||
            "Une erreur est survenue lors du changement de plan",
        );
      }
    } catch (err) {
      setSwapError(
        err.response?.data?.message || "Impossible de changer de plan",
      );
      console.error(err);
    } finally {
      setSwapLoading(false);
    }
  };
  // Fermer le modal de swap
  const closeSwapModal = () => {
    setSwapModal({ isOpen: false, plan: null });
    setSwapError(null);
    setSwapSuccess(false);
    setSwapLoading(false);
  };

  // Vérifier si l'utilisateur peut passer à un plan
  const isUpgrade = (targetPlan) => {
    if (!activeSubscriptionDetails) return false;

    return (
      parseFloat(targetPlan.stripe_plan_price) >
      parseFloat(activeSubscriptionDetails.stripe_plan_price)
    );
  };

  // Calculer la différence de prix
  const getPriceDifference = (targetPlan) => {
    if (!activeSubscriptionDetails) return 0;

    const currentPrice = parseFloat(
      activeSubscriptionDetails.stripe_plan_price,
    );
    const targetPrice = parseFloat(targetPlan.stripe_plan_price);

    return targetPrice - currentPrice;
  };

  // Formater le prix en dollars
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(price)
      .replace("$", "");
  };

  // Formater la date
  const formatDate = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  const hasActiveSubscription = () => {
    return currentSubscription !== null;
  };

  const getPlanPrice = (plan, cycle) => {
    if (plan.period_name === "lifetime") {
      return { price: plan.stripe_plan_price, period: "/à vie" };
    }

    if (cycle === "monthly" && plan.period_name === "monthly") {
      return { price: plan.stripe_plan_price, period: "/mois" };
    } else if (cycle === "annual" && plan.period_name === "yearly") {
      return { price: plan.stripe_plan_price, period: "/an" };
    }
    return null;
  };

  const hasBothCycles = (planGroup) => {
    return (
      planGroup.some((p) => p.period_name === "monthly") &&
      planGroup.some((p) => p.period_name === "yearly")
    );
  };

  const calculateSavingsPercentage = () => {
    const firstPlanGroup = Object.values(groupedPlans).find((group) =>
      hasBothCycles(group),
    );

    if (!firstPlanGroup) return 0;

    const monthlyPlan = firstPlanGroup.find((p) => p.period_name === "monthly");
    const yearlyPlan = firstPlanGroup.find((p) => p.period_name === "yearly");

    if (!monthlyPlan || !yearlyPlan) return 0;

    const monthlyTotal = monthlyPlan.stripe_plan_price * 12;
    const yearlyTotal = yearlyPlan.stripe_plan_price;
    const savings = ((monthlyTotal - yearlyTotal) / monthlyTotal) * 100;

    return Math.round(savings);
  };

  const getMonthlyEquivalent = (annualPrice) => {
    return Math.round(annualPrice / 12);
  };

  const getPlanInitial = (planName) => {
    return planName ? planName.charAt(0).toUpperCase() : "?";
  };

  const isLifetimePlan = (plan) => {
    return plan.period_name === "lifetime";
  };

  const isUnlimited = (quantity) => {
    return quantity === UNLIMITED_KEYWORD || quantity === "Illimité";
  };

  const formatFeatureName = (content) => {
    if (isUnlimited(content.quantity)) {
      return (
        <span className="flex items-center gap-1">
          <span>
            {content.subscription_param_name} illimité
            {content.subscription_param_name.endsWith("s") ? "" : "s"}
          </span>
        </span>
      );
    }

    const quantity = parseFloat(content.quantity);
    if (!isNaN(quantity)) {
      if (quantity > 1) {
        return `${quantity} ${content.subscription_param_name}`;
      }
      if (quantity === 1) {
        return `1 ${content.subscription_param_name}`;
      }
    }

    return content.subscription_param_name;
  };

  const recommendedPlanName = getRecommendedPlanName();

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* En-tête */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {hasActiveSubscription()
                ? "Mon abonnement"
                : "Choisissez votre formule"}
            </h1>
            <p className="text-gray-600 mt-1">
              {hasActiveSubscription()
                ? "Gérez votre abonnement ou changez de formule"
                : "Sélectionnez le plan qui correspond le mieux à vos besoins"}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {hasActiveSubscription() && (
              <button
                onClick={() => navigate("/cfp/subscription/manage")}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <Settings className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
                <span className="text-gray-700 group-hover:text-gray-900">
                  Gérer
                </span>
              </button>
            )}
            <button
              onClick={() => navigate("/cfp/subscription/history")}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <History className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
              <span className="text-gray-700 group-hover:text-gray-900">
                Historique
              </span>
            </button>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && <ErrorAlert message={error} onRetry={fetchPlans} />}

        {/* Current Plan */}
        {currentSubscription && activeSubscriptionDetails && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div
              className={`px-6 py-4 border-b border-gray-200 ${
                currentSubscription.stripe_status === "incomplete"
                  ? "bg-yellow-50"
                  : "bg-gradient-to-r from-blue-50 to-indigo-50"
              }`}
            >
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                {currentSubscription.stripe_status === "active" ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : currentSubscription.stripe_status === "incomplete" ? (
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                {currentSubscription.stripe_status === "active"
                  ? "Abonnement actuel"
                  : currentSubscription.stripe_status === "incomplete"
                    ? "Abonnement en attente de paiement"
                    : "Abonnement actuel"}
              </h2>
            </div>

            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-bold text-blue-600">
                      {getPlanInitial(activeSubscriptionDetails.name)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Plan actuel</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {activeSubscriptionDetails.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          currentSubscription.stripe_status === "active"
                            ? "bg-green-100 text-green-700"
                            : currentSubscription.stripe_status === "incomplete"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {currentSubscription.stripe_status === "active"
                          ? "Actif"
                          : currentSubscription.stripe_status === "incomplete"
                            ? "Paiement en attente"
                            : currentSubscription.stripe_status}
                      </span>
                      <span className="text-sm text-gray-500">
                        $
                        {formatPrice(
                          activeSubscriptionDetails.stripe_plan_price,
                        )}
                        {activeSubscriptionDetails.period_name === "monthly"
                          ? "/mois"
                          : activeSubscriptionDetails.period_name === "yearly"
                            ? "/an"
                            : "/à vie"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => navigate("/cfp/subscription/manage")}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    Gérer l'abonnement
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Grille d'informations */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    {currentSubscription.stripe_status === "active" ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Statut</p>
                    <p className="font-medium text-gray-800">
                      {currentSubscription.stripe_status === "active"
                        ? "Actif"
                        : currentSubscription.stripe_status === "incomplete"
                          ? "En attente de paiement"
                          : currentSubscription.stripe_status}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Prochain paiement</p>
                    <p className="font-medium text-gray-800">
                      {isLifetimePlan(activeSubscriptionDetails)
                        ? "Aucun (à vie)"
                        : currentSubscription.stripe_status === "incomplete"
                          ? "En attente de validation"
                          : "15 mars 2024"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <History className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Depuis le</p>
                    <p className="font-medium text-gray-800">1 mars 2024</p>
                  </div>
                </div>
              </div>

              {/* Message d'avertissement pour les abonnements incomplets */}
              {currentSubscription.stripe_status === "incomplete" && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Votre abonnement est en attente de paiement. Veuillez
                    finaliser le paiement pour activer votre abonnement.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section des plans */}
        <div className="bg-white p-8 rounded-lg shadow-sm border">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {currentSubscription ? "Changer d'offre" : "Nos offres"}
          </h2>

          {/* Billing Cycle Toggle */}
          {Object.values(groupedPlans).some((group) =>
            hasBothCycles(group),
          ) && (
            <div className="flex justify-center items-center gap-4 mb-10">
              <span
                className={`font-semibold transition-colors ${billingCycle === "monthly" ? "text-blue-600" : "text-gray-500"}`}
              >
                Mensuel
              </span>
              <button
                onClick={() =>
                  setBillingCycle(
                    billingCycle === "monthly" ? "annual" : "monthly",
                  )
                }
                className="w-12 h-6 rounded-full p-1 flex items-center transition-colors bg-blue-600"
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full transform transition-transform ${billingCycle === "annual" ? "translate-x-6" : ""}`}
                />
              </button>
              <span
                className={`font-semibold transition-colors ${billingCycle === "annual" ? "text-blue-600" : "text-gray-500"}`}
              >
                Annuel
              </span>
              {billingCycle === "annual" && (
                <span className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full animate-pulse">
                  -{calculateSavingsPercentage()}% Économie
                </span>
              )}
            </div>
          )}

          {/* Plans Grid */}
          <div className="flex items-stretch justify-center gap-8 w-full flex-wrap">
            {Object.entries(groupedPlans).map(
              ([planName, planGroup], groupIndex) => {
                const planColor = generatePlanColor(groupIndex);
                const isRecommended = planName === recommendedPlanName;

                return planGroup.map((plan) => {
                  const planPrice = isLifetimePlan(plan)
                    ? { price: plan.stripe_plan_price, period: "/à vie" }
                    : getPlanPrice(plan, billingCycle);

                  if (!planPrice && !isLifetimePlan(plan)) return null;

                  const isCurrentPlan =
                    (isLifeTimeProductActive &&
                      plan.stripe_price_id ===
                        lifetimeProduct?.stripe_price_id) ||
                    (currentSubscription &&
                      currentSubscription.stripe_price ===
                        plan.stripe_price_id);

                  const planFeatures = featuresByPlan[plan.id] || {
                    included: [],
                    limits: [],
                    params: [],
                  };

                  const allPlanFeatures = [
                    ...planFeatures.included,
                    ...planFeatures.limits,
                    ...planFeatures.params,
                  ];

                  const isHighlighted =
                    !isCurrentPlan && isRecommended && !isLifetimePlan(plan);

                  const isUpgradePlan =
                    currentSubscription &&
                    activeSubscriptionDetails &&
                    parseFloat(plan.stripe_plan_price) >
                      parseFloat(activeSubscriptionDetails.stripe_plan_price);

                  return (
                    <div
                      key={plan.id}
                      className={`w-80 p-6 rounded-2xl border relative flex flex-col ${
                        isCurrentPlan
                          ? "border-green-500 bg-green-50"
                          : isHighlighted
                            ? "border-2 border-blue-600"
                            : "border-gray-200"
                      }`}
                    >
                      {/* Badges */}
                      {isCurrentPlan && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                            PLAN ACTUEL
                          </span>
                        </div>
                      )}

                      {!isCurrentPlan && isHighlighted && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                            POPULAIRE
                          </span>
                        </div>
                      )}

                      <h3 className="text-xl font-semibold text-center">
                        {planName}
                      </h3>

                      <p className="text-sm text-gray-500 mt-2 text-center h-10">
                        {isLifetimePlan(plan)
                          ? "Un paiement unique, un accès à vie."
                          : `Abonnement ${planName}`}
                      </p>

                      <div className="my-8 text-center">
                        <span className="text-5xl font-extrabold">
                          ${formatPrice(planPrice.price)}
                        </span>
                        <span className="text-xl font-medium text-gray-500">
                          {planPrice.period}
                        </span>

                        {billingCycle === "annual" && !isLifetimePlan(plan) && (
                          <p className="text-sm text-green-600 mt-2">
                            Soit $
                            {formatPrice(getMonthlyEquivalent(planPrice.price))}
                            /mois
                          </p>
                        )}

                        {/* Affichage de la différence de prix pour les swaps */}
                        {currentSubscription &&
                          !isCurrentPlan &&
                          activeSubscriptionDetails && (
                            <p
                              className={`text-xs mt-2 ${
                                isUpgradePlan
                                  ? "text-teal-600"
                                  : "text-gray-600"
                              }`}
                            >
                              {isUpgradePlan
                                ? `+$${formatPrice(getPriceDifference(plan))} par rapport à votre plan actuel`
                                : `-$${formatPrice(Math.abs(getPriceDifference(plan)))} par rapport à votre plan actuel`}
                            </p>
                          )}
                      </div>
                      {!isLifeTimeProductActive && (
                        <button
                          onClick={() =>
                            currentSubscription
                              ? handleSwapPlan(plan)
                              : handlePlanSelection(plan)
                          }
                          disabled={
                            processingPlan === plan.id ||
                            isCurrentPlan ||
                            (currentSubscription &&
                              currentSubscription.stripe_status ===
                                "incomplete")
                          }
                          className={`w-full py-3 font-semibold rounded-lg transition-colors relative mt-auto ${
                            isCurrentPlan
                              ? "bg-green-600 text-white cursor-default hover:bg-green-700"
                              : processingPlan === plan.id
                                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                : currentSubscription && isUpgradePlan
                                  ? "bg-teal-600 text-white hover:bg-teal-700"
                                  : currentSubscription &&
                                      !isUpgradePlan &&
                                      !isCurrentPlan
                                    ? "bg-gray-600 text-white hover:bg-gray-700"
                                    : isHighlighted
                                      ? "bg-blue-600 text-white hover:bg-blue-700"
                                      : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                          }`}
                        >
                          {isCurrentPlan ? (
                            <>
                              <CheckCircle className="w-5 h-5 inline-block mr-2" />
                              Plan actuel
                            </>
                          ) : processingPlan === plan.id ? (
                            "Chargement..."
                          ) : currentSubscription ? (
                            <>
                              <RefreshCw className="w-4 h-4 inline-block mr-2" />
                              {isUpgradePlan
                                ? "Passer à ce plan"
                                : "Changer pour ce plan"}
                            </>
                          ) : (
                            "Choisir ce plan"
                          )}
                        </button>
                      )}

                      <ul className="space-y-3 text-sm mt-8">
                        {/* Features communes */}
                        {commonFeatures.length > 0 &&
                          commonFeatures.map((feature, index) => (
                            <li
                              key={`common-${index}`}
                              className="flex items-center gap-3"
                            >
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-gray-700">
                                {formatFeatureName(feature)}
                              </span>
                            </li>
                          ))}

                        {/* Features spécifiques au plan */}
                        {allPlanFeatures.map((content, index) => {
                          const isCommon = commonFeatures.some(
                            (cf) =>
                              cf.subscription_param_name ===
                              content.subscription_param_name,
                          );

                          if (isCommon) return null;

                          return (
                            <li
                              key={`specific-${index}`}
                              className="flex items-center gap-3"
                            >
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-gray-700">
                                {formatFeatureName(content)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                });
              },
            )}
          </div>

          {/* Message si aucun plan disponible */}
          {plans.length === 0 && !error && (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                Aucun plan disponible pour le moment.
              </p>
            </div>
          )}
        </div>

        {/* Modal de confirmation de swap */}
        {swapModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Confirmer le changement de plan
              </h3>

              {swapSuccess ? (
                <div className="text-center py-4">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-green-600 font-semibold mb-2">
                    Plan changé avec succès !
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Votre abonnement a été mis à jour.
                  </p>
                  <button
                    onClick={closeSwapModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Fermer
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <p className="text-gray-700 mb-4">
                      Vous êtes sur le point de passer de :
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <p className="font-semibold text-gray-900">
                        {activeSubscriptionDetails?.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        $
                        {formatPrice(
                          activeSubscriptionDetails?.stripe_plan_price,
                        )}
                        {activeSubscriptionDetails?.period_name === "monthly"
                          ? "/mois"
                          : activeSubscriptionDetails?.period_name === "yearly"
                            ? "/an"
                            : "/à vie"}
                      </p>
                    </div>

                    <p className="text-gray-700 mb-2">À :</p>
                    <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                      <p className="font-semibold text-gray-900">
                        {swapModal.plan?.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        ${formatPrice(swapModal.plan?.stripe_plan_price)}
                        {swapModal.plan?.period_name === "monthly"
                          ? "/mois"
                          : swapModal.plan?.period_name === "yearly"
                            ? "/an"
                            : "/à vie"}
                      </p>
                    </div>

                    {swapError && (
                      <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        <p className="text-sm">{swapError}</p>
                      </div>
                    )}

                    {activeSubscriptionDetails && swapModal.plan && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          {isUpgrade(swapModal.plan) ? (
                            <>
                              La différence de prix sera facturée immédiatement
                              au prorata.
                            </>
                          ) : (
                            <>
                              Vous serez crédité du montant restant sur votre
                              prochaine facture.
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={closeSwapModal}
                      disabled={swapLoading}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={confirmSwap}
                      disabled={swapLoading}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                    >
                      {swapLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Traitement...
                        </>
                      ) : (
                        "Confirmer"
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Section FAQ */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Questions fréquentes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="font-medium text-gray-800 mb-2">
                Puis-je changer de plan ?
              </p>
              <p className="text-sm text-gray-600">
                Oui, vous pouvez passer à un plan supérieur ou changer votre
                cycle de facturation à tout moment.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-800 mb-2">
                Comment annuler ?
              </p>
              <p className="text-sm text-gray-600">
                Rendez-vous dans la section "Gérer" pour annuler votre
                abonnement. L'annulation prendra effet à la fin de votre période
                en cours.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-800 mb-2">
                Quels moyens de paiement ?
              </p>
              <p className="text-sm text-gray-600">
                Nous acceptons toutes les cartes bancaires (Visa, Mastercard,
                American Express) et PayPal.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-800 mb-2">Une garantie ?</p>
              <p className="text-sm text-gray-600">
                Nous offrons une garantie satisfait ou remboursé de 30 jours
                sans condition.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionProfilePage;
